import React, { useState, useEffect } from "react";
import * as Sentry from '@sentry/react';
import Profile from "./Profile";
import "./style.css";
import "react-google-reviews/dist/index.css";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Header from './Header';

// When running locally (npm start) hit the dev guest-service so we can preview
// dev data (e.g. testco); production builds continue to use the prod API.
const API_BASE = process.env.NODE_ENV === 'development'
	? 'https://ah7cxw1aa6.execute-api.eu-west-1.amazonaws.com/dev'
	: 'https://c9rtj2s6a4.execute-api.eu-west-1.amazonaws.com/prod';


const getTheme = (color) => createTheme({
  palette: {
	primary: { main: color || '#229626', contrastText: "#fff" },
  },
});


const App = (props) => {
	const [profileData, setProfileData] = useState(null);
	const [error, setError] = useState(null);
	const [notFound, setNotFound] = useState(false);
	// Determine preview mode via path '/preview-profile' with slug provided as a query param (?slug=...)
	const pathName = typeof window !== 'undefined' ? window.location.pathname : '';
	const search = typeof window !== 'undefined' ? window.location.search : '';
	const preview = pathName === '/preview-profile';
	const theme = getTheme(props.color);

	useEffect(() => {
		async function fetchProfile() {
			let slug;
			try {
				// Reset transient state before each fetch so moving between preview/non-preview updates cleanly
				setError(null);
				setNotFound(false);
				setProfileData(null);
				const mainSiteUrl = 'https://energyefficiency.ie';
				const pathSegments = window.location.pathname.split('/').filter(Boolean);

				// Redirect root (no path segments) to main site
				if (pathSegments.length === 0) {
					window.location.replace(mainSiteUrl);
					return;
				}

				if (preview) {
					// Read slug from query string e.g. /preview-profile?slug=company-x
					const params = new URLSearchParams(search);
					slug = params.get('slug');
				} else {
					slug = pathSegments[pathSegments.length - 1];
				}

				if (!slug) {
					setNotFound(true);
					return;
				}

				const response = await fetch(`${API_BASE}/companies/get-by-slug`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ slug })
				});
				if (!response.ok) throw new Error(`Request failed: ${response.status}`);
				const data = await response.json();
				console.log("Fetched profile data:", data);
				if (!data || Object.keys(data).length === 0) {
					setNotFound(true);
					return;
				}
				// Only allow rendering unpublished profiles in preview mode
				const isPublished = (data.profileStatus || '').trim().toLowerCase() === 'published';
				if (!preview && !isPublished) {
					setNotFound(true);
					return;
				}
				setProfileData(data);
			} catch (err) {
				console.error("Error fetching profile data:", err);
				Sentry.captureException(err, { extra: { slug: slug || 'unknown', preview } });
				setError(err.message);
				setNotFound(true);
			}
		}
		fetchProfile();
	}, [preview, search]);

	//console.log("profileData", profileData);
	return (
		<ThemeProvider theme={theme}>
			<div id="react-app">
				<Header />
				{/* Loading */}
				{!notFound && !profileData && !error && (
					<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
						<CircularProgress color="primary" />
					</div>
				)}
				{/* Not Found / Error */}
				{notFound && (
					<div style={{ textAlign: 'center', padding: '80px 20px' }}>
						<h2 style={{ margin: '0 0 12px', color: '#023a51', fontSize: '30px', fontWeight: 700 }}>Sorry, this page does not exist</h2>
						<p style={{ margin: '0 0 32px', color: '#555', fontSize: '16px' }}>
							{error ? 'An error occurred while attempting to load this profile.' : 'The installer profile you are trying to view could not be found.'}
						</p>
						<button
							onClick={() => { window.location.href = 'https://energyefficiency.ie'; }}
							style={{
								background: '#023a51',
								color: '#fff',
								border: 'none',
								padding: '14px 30px',
								cursor: 'pointer',
								borderRadius: '32px',
								fontSize: '16px',
								fontWeight: 600,
								letterSpacing: '.3px'
							}}
						>
							Back to Energy Efficiency 
						</button>
					</div>
				)}
				{/* Profile */}
				{!notFound && profileData && <Profile {...profileData} />}
			</div>
		</ThemeProvider>
	);
};

export default App;